import { useCallback, useContext, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { ClosedCaptionAltIcon } from '@hugeicons/core-solid-rounded'
import * as Popover from '@radix-ui/react-popover'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import { popoverContainer } from '@/utils/popovers'
import { useBranding } from '@/features/branding'
import Button from '@/components/ui/Button'

import {
  formToGraphql,
  FormTranslations,
  graphqlToForm
} from '@/features/translations'

const TRANSLATABLE_FIELDS = ['altText']

type AltTextData = {
  altText: string
  translations: FormTranslations
}

interface Props {
  altText?: string | null
  translations?: (GraphQL.TranslationInput | GraphQL.Translation)[]
  readOnly?: boolean
  onChange: (
    altText: string | null,
    translations?: (GraphQL.TranslationInput | GraphQL.Translation)[]
  ) => void
}

function AltTextForm({
  readOnly,
  altText,
  translations,
  onChange,
  onClose
}: Props & { onClose: () => void }) {
  const { t } = useLingui()

  const {
    branding: { supportedLocales }
  } = useBranding()

  const onChangeAltText = useCallback(
    ({ altText, translations }: AltTextData) => {
      onChange(altText, formToGraphql(translations))
      onClose()
    },
    [onChange, onClose]
  )

  return (
    <Form.Root<AltTextData>
      onSubmit={onChangeAltText}
      readOnly={readOnly}
      defaultValues={{
        altText: altText ?? '',
        translations: graphqlToForm(
          translations ?? [],
          TRANSLATABLE_FIELDS,
          supportedLocales ?? []
        )
      }}
      translatableFields={TRANSLATABLE_FIELDS}
    >
      <Form.Field
        name="altText"
        label={t`Alt text`}
        description={t`The alt text is a short description of what an image shows. They are used by screen readers to help visually impaired users. (This is required in Europe under the Accessibility Act)`}
        rules={{
          maxLength: {
            value: 255,
            message: t`Can't be longer than 255 characters.`
          },
          validate: {
            required: Form.Validations.requiredWithTrimValidation(
              t`This field is required`
            )
          }
        }}
      >
        {({ renderProps: { onChange, ...props } }) => (
          <Input
            type="text"
            placeholder={t`example: A woman enjoying a massage.`}
            onChange={event => onChange(event.target.value)}
            {...props}
          />
        )}
      </Form.Field>
      <div className="flex flex-row justify-between items-center">
        <Button type="button" onClick={onClose} variant="outline">
          <Trans>Close</Trans>
        </Button>
        <Form.Submit onlyValidWhenDirty>
          <Trans>Ok</Trans>
        </Form.Submit>
      </div>
    </Form.Root>
  )
}

function AltTextPopover(props: Props) {
  const { t } = useLingui()
  const { isAnyDirty } = useContext(Form.DirtyContext)
  const [open, setOpen] = useState(false)

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (isAnyDirty) return
      setOpen(open)
    },
    [isAnyDirty, setOpen]
  )

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger
        className="rounded bg-black/50 p-1 text-white outline-primary hover:bg-black/70"
        aria-label={t`Edit Alt text`}
        title={t`Edit Alt text`}
      >
        <HugeiconsIcon icon={ClosedCaptionAltIcon} className="size-full" />
      </Popover.Trigger>
      <Popover.Portal container={popoverContainer}>
        <Popover.Content
          side="top"
          collisionPadding={20}
          sideOffset={5}
          className="relative overflow-hidden rounded bg-white shadow-xl z-popover p-3 max-w-[600px]"
        >
          <AltTextForm {...props} onClose={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export default function DirtyTextPopover(props: Props) {
  return (
    <Form.DirtyProvider>
      <AltTextPopover {...props} />
    </Form.DirtyProvider>
  )
}
